import { MAX_AUTOSUGGEST_TERMS, type ProcessedAutosuggestResults, type ProcessedVertexAutosuggestResults, processAutosuggestResults, processVertexAutosuggestResults } from '@spa/components/autosuggest/autosuggestProcessing';
import React, { ReactElement, memo, startTransition, useCallback, useEffect, useMemo, useRef } from 'react';
import trackProductActions, { ProductActionsOriginType, ProductActionsType, ProductsActionOrigin, TrackProductActionsType } from '@spa/lib/tracking/trackProductActions';
import { useDispatch, useSelector } from 'react-redux';
import { AnalyticsProduct } from '@spa/lib/tracking/productsToAnalyticsItemConverter';
import ImageComponent from '@spa/components/image-component/image.component';
import Link from 'next/link';
import { MutableRefObject } from 'preact/compat';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { PageTemplate } from '@spa/lib/cms/const';
import { Product } from '@spa/components/autosuggest/autosuggest.types';
import { SearchSuggestionType } from '@spa/lib/tracking/pushToDataLayer';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import { SuggestedCategory } from '@spa/lib/graphql/search/searchSuggestions';
import { UrlObject } from 'url';
import classNames from 'clsx';
import getAuthSessionUserId from '@spa/lib/auth/getAuthSessionUserId';
import logger from '@spa/lib/logger';
import { markSearchEventSuppressed } from '@spa/lib/tracking/searchEventSuppression';
import searchSuggestionsAction from '@spa/lib/actions/search/searchSuggestionsAction';
import selectLastQueryMetadata from '@spa/lib/store/search/selectors/selectLastQueryMetadata';
import selectPageTemplate from '@spa/lib/store/router/selectors/selectPageTemplate';
import selectSearchSettings from '@spa/lib/store/settings/selectors/selectSearchSettings';
import { setLastQueryMetadata } from '@spa/lib/store/search/searchSlice';
import { setListContext } from '@spa/lib/tracking/listContextStorage';
import styles from '@spa/components/autosuggest/autosuggest.component.module.scss';
import trackSelectSearchSuggestionActions from '@spa/lib/tracking/trackSelectSearchSuggestionActions';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
import useObjectReducer from '@spa/lib/react/hooks/useObjectReducer';
import { usePathname } from 'next/navigation';
export enum SearchState {
  INITIAL,
  SEARCHING,
  SUCCEEDED,
  ERRORED,
}
export type SuggestionSource = Product | SuggestedCategory | string;
export interface Props {
  readonly isInputFocused: boolean;
  readonly listboxId: string;
  readonly onClickOnChildElement: () => void;
  readonly queryStr: string;
  readonly pageSize: number;
  readonly pageIdx: number;
  readonly selectedIndex?: number;
  readonly onSuggestionSelect?: (suggestion: string) => void;
  readonly onSuggestionsCountChange?: (count: number) => void;
  readonly onSuggestionsUpdate?: (suggestions: Array<SuggestionSource>) => void;
}
export const NUMBER_OF_ENTRIES_DISPLAYED = 3;
const SPECIAL_CHARS_REGEX = /[+\-&|!(){}[\]^"~*?:/]/g;
const CAPS_WORD_REGEX = /\b\p{Lu}{2,}\b/gu;
const LEADING_LETTER_REGEX = /^([\s\p{Ps}\p{Pi}\p{Po}]*)(\p{L})/u;
function sortSuggestionsByNameLength(suggestions: ReadonlyArray<SuggestionSource>): Array<SuggestionSource> {
  return [...suggestions].sort((a, b) => {
    const aName = typeof a === 'string' ? a : a.name;
    const bName = typeof b === 'string' ? b : b.name;
    return aName.length - bName.length;
  });
}
function createMixedSuggestionsList(suggestedBrands: ReadonlyArray<SuggestedCategory>, suggestedCategories: ReadonlyArray<SuggestedCategory>, terms: ReadonlyArray<string>, products: ReadonlyArray<Product>): Array<SuggestionSource> {
  return sortSuggestionsByNameLength([...suggestedBrands.slice(0, NUMBER_OF_ENTRIES_DISPLAYED), ...suggestedCategories.slice(0, NUMBER_OF_ENTRIES_DISPLAYED), ...terms.slice(0, NUMBER_OF_ENTRIES_DISPLAYED), ...products.slice(0, NUMBER_OF_ENTRIES_DISPLAYED)]);
}
function AutosuggestComponent({
  isInputFocused,
  listboxId,
  onClickOnChildElement,
  queryStr,
  pageSize,
  pageIdx,
  selectedIndex = -1,
  onSuggestionSelect,
  onSuggestionsCountChange,
  onSuggestionsUpdate
}: Props): ReactElement {
  const [state, updateState] = useObjectReducer({
    prevInputFocused: false,
    prevQuery: '',
    products: [] as Array<Product>,
    searchStatus: SearchState.INITIAL,
    suggestedBrands: [] as Array<SuggestedCategory>,
    suggestedCategories: [] as Array<SuggestedCategory>,
    terms: [] as Array<string>
  });
  const {
    formatMessage
  }: NamespacedIntl = useNamespacedIntl('autosuggest');
  const pageTemplate: PageTemplate = useSelector(selectPageTemplate);
  const {
    searchSuggestionsTimeoutMS,
    vertexSearchEnabled
  } = useSelector(selectSearchSettings);
  const dispatch = useDispatch();
  const {
    authSession,
    session
  }: SessionsData = useNextAuthOrSession();
  const trimmedQueryStr: string = queryStr.trim();
  const {
    prevInputFocused,
    prevQuery,
    products,
    searchStatus,
    suggestedBrands,
    suggestedCategories,
    terms
  } = state;
  const lastQueryMetadata: Record<string, string> = useSelector(selectLastQueryMetadata);
  const lastQueryStr: MutableRefObject<string> = useRef(trimmedQueryStr);
  const lastFocusedState: MutableRefObject<boolean> = useRef(false);
  const isSubscribedRef: MutableRefObject<boolean> = useRef(true);
  if (isInputFocused !== prevInputFocused || trimmedQueryStr !== prevQuery) {
    const isFirstFocusInline = isInputFocused && !prevInputFocused;
    const hasQueryChangedInline = trimmedQueryStr !== prevQuery;
    updateState({
      prevInputFocused: isInputFocused,
      prevQuery: trimmedQueryStr
    });
    if (isFirstFocusInline || hasQueryChangedInline) {
      if (trimmedQueryStr.length === 0) {
        updateState({
          products: [],
          searchStatus: SearchState.INITIAL,
          suggestedBrands: [],
          suggestedCategories: [],
          terms: []
        });
      } else {
        updateState({
          searchStatus: SearchState.SEARCHING
        });
      }
    }
  }
  const getIsSubscribed: () => boolean = useCallback((): boolean => isSubscribedRef.current, []);
  const anonymousUserId = session.guid;
  const userId = getAuthSessionUserId(authSession);
  const pathname: string = usePathname();
  const handleSearchProductsViewedTracking = useCallback(async (products_0: Array<Product>): Promise<void> => {
    if (products_0.length) {
      const analyticsProduct: Array<AnalyticsProduct> = products_0.map((product, index) => ({
        ...product,
        index
      }));
      const listOrigin: ProductActionsOriginType = {
        originId: `suchvorschlag_${lastQueryStr.current.replace(/\s/g, '_')}`,
        originType: 'Suchvorschläge'
      };
      trackProductActions({
        type: ProductActionsType.VIEW_SEARCH_ITEMS,
        actionOrigin: listOrigin
      }, analyticsProduct, anonymousUserId, userId, {
        pathname,
        pageTemplate
      }, lastQueryMetadata);
    }
  }, [anonymousUserId, pageTemplate, lastQueryMetadata, pathname, userId]);
  const allSuggestionsList: Array<SuggestionSource> = useMemo(() => vertexSearchEnabled ? terms.slice(0, MAX_AUTOSUGGEST_TERMS) : createMixedSuggestionsList(suggestedBrands, suggestedCategories, terms, products), [vertexSearchEnabled, terms, suggestedBrands, suggestedCategories, products]);
  const searchSuggestionsCallback = useCallback(async (isSubscribedGetter: () => boolean): Promise<void> => {
    try {
      const {
        popularQueries,
        products: result,
        suggestedCategories: suggestedCategories_0,
        suggestedBrands: suggestedBrands_0
      } = await searchSuggestionsAction('searchSuggestionsAction', {
        queryStr: trimmedQueryStr,
        pageSize,
        pageIdx
      });
      if (isSubscribedGetter() && vertexSearchEnabled) {
        const {
          searchTerms
        }: ProcessedVertexAutosuggestResults = processVertexAutosuggestResults(popularQueries);
        startTransition(() => {
          updateState({
            products: [],
            searchStatus: SearchState.SUCCEEDED,
            suggestedBrands: [],
            suggestedCategories: [],
            terms: searchTerms
          });
          onSuggestionsCountChange?.(searchTerms.length);
          onSuggestionsUpdate?.(searchTerms);
        });
      } else if (isSubscribedGetter() && result.__typename === 'SearchProductResultsSucceeded') {
        const searchProducts = result.results.map(product_0 => ({
          ...product_0,
          strikePrice: null
        }));
        const {
          products: products_1,
          searchTerms: searchTerms_0,
          categories,
          brands
        }: ProcessedAutosuggestResults = processAutosuggestResults(popularQueries, suggestedBrands_0, suggestedCategories_0, searchProducts);
        const updatedSuggestions: Array<SuggestionSource> = createMixedSuggestionsList(brands, categories, searchTerms_0, products_1);
        startTransition(() => {
          updateState({
            products: products_1,
            searchStatus: SearchState.SUCCEEDED,
            suggestedBrands: brands,
            suggestedCategories: categories,
            terms: searchTerms_0
          });
          onSuggestionsCountChange?.(updatedSuggestions.length);
          onSuggestionsUpdate?.(updatedSuggestions);
        });
        await handleSearchProductsViewedTracking(searchProducts);
        dispatch(setLastQueryMetadata({
          lastQueryMetadata: result.metadata
        }));
      }
    } catch (e) {
      logger.error(e, 'Error getting search suggestions');
      if (isSubscribedGetter()) {
        updateState({
          products: [],
          searchStatus: vertexSearchEnabled ? SearchState.SUCCEEDED : SearchState.ERRORED,
          suggestedBrands: [],
          suggestedCategories: [],
          terms: []
        });
        onSuggestionsCountChange?.(0);
        onSuggestionsUpdate?.([]);
      }
    }
  }, [dispatch, trimmedQueryStr, pageSize, pageIdx, vertexSearchEnabled, handleSearchProductsViewedTracking, onSuggestionsCountChange, onSuggestionsUpdate, updateState]);
  useEffect(() => {
    isSubscribedRef.current = true;
    const isFirstFocus: boolean = isInputFocused && !lastFocusedState.current;
    const hasQueryStrChanged: boolean = lastQueryStr.current !== trimmedQueryStr;
    if (isFirstFocus || hasQueryStrChanged) {
      lastQueryStr.current = trimmedQueryStr;
      lastFocusedState.current = isInputFocused;
      if (trimmedQueryStr.length === 0) {
        onSuggestionsCountChange?.(0);
        onSuggestionsUpdate?.([]);
      } else {
        const timer: NodeJS.Timeout = setTimeout(async (): Promise<void> => {
          if (getIsSubscribed()) {
            await searchSuggestionsCallback(getIsSubscribed);
          }
        }, searchSuggestionsTimeoutMS);
        return (): void => {
          isSubscribedRef.current = false;
          clearTimeout(timer);
        };
      }
    }
  }, [isInputFocused, trimmedQueryStr, searchSuggestionsCallback, getIsSubscribed, searchSuggestionsTimeoutMS, onSuggestionsCountChange, onSuggestionsUpdate]);
  const noResults: boolean = allSuggestionsList.length === 0;
  const autosuggestClass = classNames(styles.autosuggest, {
    [styles['autosuggest--active']]: isInputFocused && queryStr.length > 0,
    [styles['autosuggest--searching']]: searchStatus === SearchState.SEARCHING,
    [styles['autosuggest--succeeded']]: searchStatus === SearchState.SUCCEEDED,
    [styles['autosuggest--errored']]: searchStatus === SearchState.ERRORED
  });
  const contentClass = classNames(styles.autosuggest__content, {
    [styles['autosuggest__content--active']]: !noResults
  });
  const handleProductClick = useCallback((product_1: Product, index_0: number): void => {
    const originType = ProductsActionOrigin.SEARCH_SUGGESTION;
    const trackingOriginData: ProductActionsOriginType = {
      originId: originType.toString(),
      originType
    };
    const eventType: TrackProductActionsType = {
      type: ProductActionsType.SELECT_ITEM,
      actionOrigin: trackingOriginData
    };
    trackProductActions(eventType, [{
      ...product_1,
      index: index_0
    }], anonymousUserId, userId, {
      pathname,
      pageTemplate
    }, lastQueryMetadata);
  }, [anonymousUserId, pageTemplate, lastQueryMetadata, pathname, userId]);
  const isProduct = useCallback((source: SuggestionSource): source is Product => typeof source !== 'string' && 'currentPrice' in source, []);
  const escapeRegExp = useCallback((input: string): string => {
    return input.replace(SPECIAL_CHARS_REGEX, '\\$&');
  }, []);
  const normalizeCaps = useCallback((text: string): string => {
    let result_0 = text.replace(CAPS_WORD_REGEX, word => word.charAt(0) + word.slice(1).toLowerCase());
    result_0 = result_0.replace(LEADING_LETTER_REGEX, (_, prefix = '', firstLetter) => prefix + firstLetter.toUpperCase());
    return result_0;
  }, []);
  const highlightRegex: RegExp = useMemo(() => new RegExp(`(${escapeRegExp(trimmedQueryStr)})`, 'gi'), [escapeRegExp, trimmedQueryStr]);
  const getHighlightedText = useCallback((rawText: string, highlight: string): ReactElement => {
    const text_0 = normalizeCaps(rawText);
    const parts = text_0.split(highlightRegex);
    let offset = 0;
    return <>
          {parts.map(part => {
        const key = `${offset}-${part}`;
        offset += part.length;
        return part.toLowerCase() === highlight.toLowerCase() ? <mark key={key}>{part}</mark> : <span key={key}>{part}</span>;
      })}
        </>;
  }, [highlightRegex, normalizeCaps]);
  const renderHighlightedText = useCallback((highlightSnippet: string | null | undefined, fallbackText: string): ReactElement => {
    if (!highlightSnippet) {
      return <span>{fallbackText}</span>;
    }
    const parts_0 = highlightSnippet.split(/(<mark>.*?<\/mark>)/g);
    let offset_0 = 0;
    return <>
          {parts_0.map(part_0 => {
        const key_0 = `${offset_0}-${part_0}`;
        offset_0 += part_0.length;
        return part_0.startsWith('<mark>') ? <mark key={key_0}>{part_0.replace(/<\/?mark>/g, '')}</mark> : <span key={key_0}>{part_0}</span>;
      })}
        </>;
  }, []);
  const generateEntries = useCallback((source_0: SuggestionSource, index_1: number): ReactElement => {
    let code: string;
    let highlightSnippet_0: string | null | undefined;
    let highlightText: ReactElement;
    let name: string;
    let path: string | UrlObject;
    let searchSuggestionType: SearchSuggestionType;
    if (typeof source_0 === 'string') {
      code = source_0;
      highlightSnippet_0 = null;
      highlightText = getHighlightedText(source_0, trimmedQueryStr);
      name = source_0;
      path = {
        pathname: '/search/',
        query: {
          q: source_0
        }
      };
      searchSuggestionType = SearchSuggestionType.SEARCH_TERM;
    } else {
      code = source_0.code;
      highlightSnippet_0 = source_0.highlightSnippet;
      highlightText = renderHighlightedText(highlightSnippet_0, source_0.name);
      name = source_0.name;
      path = source_0.path;
      searchSuggestionType = 'type' in source_0 ? SearchSuggestionType[source_0.type] : SearchSuggestionType.PRODUCT;
    }
    const isSelected: boolean = index_1 === selectedIndex;
    const entryClass: string = classNames(styles.autosuggest__entry, {
      [styles['autosuggest__entry--highlighted']]: isSelected
    });
    return <Link prefetch={false} data-testid={`autosuggest__entry-link-${code}`} href={path} key={`${searchSuggestionType}-${code}`} id={`${listboxId}-option-${index_1}`} role="option" aria-selected={isSelected} onClick={(): void => {
      if (isProduct(source_0)) {
        const trackingOriginData_0: ProductActionsOriginType = {
          originId: ProductsActionOrigin.SEARCH_SUGGESTION,
          originType: ProductsActionOrigin.SEARCH_SUGGESTION
        };
        setListContext(source_0.code, trackingOriginData_0);
        handleProductClick(source_0, index_1);
      }
      if (typeof source_0 === 'string') {
        sessionStorage.setItem('restoreSearchFocus', 'true');
        markSearchEventSuppressed();
        if (vertexSearchEnabled) {
          onSuggestionSelect?.(source_0);
        }
      }
      onClickOnChildElement();
      trackSelectSearchSuggestionActions(name, trimmedQueryStr, searchSuggestionType, {
        pathname,
        pageTemplate
      });
    }} aria-label={name} data-track-id="suggestSearchTerm">
          <div className={entryClass}>
            <ImageComponent src="/assets/icons/search-grey.svg" alt="" width={24} height={24} className={styles.autosuggest__icon} />
            <span className={styles['autosuggest__entry-part']}>{highlightText}</span>
          </div>
        </Link>;
  }, [getHighlightedText, handleProductClick, isProduct, listboxId, pageTemplate, onClickOnChildElement, onSuggestionSelect, pathname, renderHighlightedText, trimmedQueryStr, selectedIndex, vertexSearchEnabled]);
  const generateEntrySet = useCallback((source_1: Array<SuggestionSource>): ReactElement => {
    return <div className={styles['autosuggest__result-set']}>
          {source_1.map((item, index_2) => {
        return generateEntries(item, index_2);
      })}
        </div>;
  }, [generateEntries]);
  return <>
      <section className={autosuggestClass} id={listboxId}>
        <div aria-busy={searchStatus === SearchState.SEARCHING} title={formatMessage('loadingBar')} />
        <div className={contentClass}>
          {allSuggestionsList.length > 0 && generateEntrySet(allSuggestionsList)}
        </div>
      </section>
      <div className="sr-only" role="status" aria-live="assertive" aria-atomic="true">
        {isInputFocused && searchStatus === SearchState.SUCCEEDED && allSuggestionsList.length > 0 && <span>
              {formatMessage('suggestionsAvailable', {
          count: allSuggestionsList.length
        })}
            </span>}
        {isInputFocused && searchStatus === SearchState.SUCCEEDED && noResults && <span>{formatMessage('noSuggestions')}</span>}
      </div>
    </>;
}
export default memo(AutosuggestComponent);